import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

// --- Column Definition ---

export interface QColumnDef<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
  locked?: boolean;
  defaultVisible?: boolean;
  width?: number;
  className?: string;
}

export interface QColumnConfig {
  key: string;
  visible: boolean;
  order: number;
}

// --- Quick Filters ---

export interface QQuickFilterOption {
  value: string;
  label: string;
  color?: string;
}

export interface QQuickFilter {
  key: string;
  label?: string;
  options: QQuickFilterOption[] | string[];
}

// --- Advanced Filters ---

export interface QFilterDef {
  key: string;
  label: string;
  type: 'select' | 'multi-select' | 'text' | 'date-range';
  options?: { value: string; label: string }[];
  placeholder?: string;
}

export interface QFilterSection {
  key: string;
  title: string;
  filters: QFilterDef[];
}

// --- Row Actions ---

export interface QRowAction<T> {
  label: string;
  icon?: LucideIcon;
  onClick: (row: T) => void;
  variant?: 'default' | 'destructive';
  show?: (row: T) => boolean;
}

// --- Main Props ---

export interface QDataTableProps<T> {
  // Data
  data: T[];
  totalItems: number;
  isLoading?: boolean;
  getId: (row: T) => string | number;

  // Columns
  columns: QColumnDef<T>[];
  columnConfigs?: QColumnConfig[];
  onColumnConfigsChange?: (configs: QColumnConfig[]) => void;

  // Sorting (server-side)
  sort?: string;
  order?: 'asc' | 'desc';
  onSortChange?: (field: string, order: 'asc' | 'desc') => void;

  // Pagination (server-side)
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;

  // Search
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;

  // Quick Filters
  quickFilters?: QQuickFilter[];
  quickFilterValues?: Record<string, string[]>;
  onQuickFilterChange?: (key: string, values: string[]) => void;

  // Advanced Filters
  filterSections?: QFilterSection[];
  filterValues?: Record<string, unknown>;
  onApplyFilters?: (values: Record<string, unknown>) => void;

  // Row Interaction
  onRowClick?: (row: T) => void;
  rowActions?: QRowAction<T>[];
  selectable?: boolean;
  selectedIds?: (string | number)[];
  onSelectionChange?: (ids: (string | number)[]) => void;

  // Header
  title?: string;
  headerActions?: ReactNode;
  emptyMessage?: string;
  emptyIcon?: LucideIcon;
  itemLabel?: string;
}

// --- Helpers ---

export function normalizeQuickFilterOptions(
  options: QQuickFilterOption[] | string[]
): QQuickFilterOption[] {
  return options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );
}

export function buildColumnConfigs<T>(
  columns: QColumnDef<T>[],
  existing?: QColumnConfig[]
): QColumnConfig[] {
  if (existing?.length) return existing;
  return columns.map((col, i) => ({
    key: col.key,
    visible: col.defaultVisible !== false,
    order: i,
  }));
}

export function getVisibleColumns<T>(
  columns: QColumnDef<T>[],
  configs: QColumnConfig[]
): QColumnDef<T>[] {
  const configMap = new Map(configs.map((c) => [c.key, c]));
  return columns
    .filter((col) => {
      const cfg = configMap.get(col.key);
      return cfg ? cfg.visible : col.defaultVisible !== false;
    })
    .sort((a, b) => {
      const aOrder = configMap.get(a.key)?.order ?? 0;
      const bOrder = configMap.get(b.key)?.order ?? 0;
      return aOrder - bOrder;
    });
}
